import { motion } from 'framer-motion';
import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { getPortalRoleConfig, getSiteRoleLabel } from '../data/siteUsers';
import { useSiteAuth } from '../hooks/useSiteAuth';
import { getSiteSettings, updateSiteSettings } from '../services/site-settings';
import {
  getStripeSettings,
  startStripeConnectOnboarding,
  updateStripeSettings,
  type StripeSettings,
} from '../services/stripe-settings';

export default function PortalAccount() {
  const { session, runtime, changePassword } = useSiteAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stripeSettings, setStripeSettings] = useState<StripeSettings | null>(null);
  const [stripeMode, setStripeMode] = useState<'test' | 'live'>('test');
  const [connectAccountId, setConnectAccountId] = useState('');
  const [stripeError, setStripeError] = useState<string | null>(null);
  const [stripeSuccess, setStripeSuccess] = useState<string | null>(null);
  const [isLoadingStripe, setIsLoadingStripe] = useState(false);
  const [isSavingStripe, setIsSavingStripe] = useState(false);
  const [isLaunchingOnboarding, setIsLaunchingOnboarding] = useState(false);
  const [siteName, setSiteName] = useState('');
  const [notificationEmail, setNotificationEmail] = useState('');
  const [isLoadingSiteSettings, setIsLoadingSiteSettings] = useState(false);
  const [isSavingSiteSettings, setIsSavingSiteSettings] = useState(false);
  const [siteSettingsError, setSiteSettingsError] = useState<string | null>(null);
  const [siteSettingsSuccess, setSiteSettingsSuccess] = useState<string | null>(null);

  const roleConfig = getPortalRoleConfig(session?.role ?? 'read_only');
  const canManageStripe = session?.role === 'admin';
  const canManageSiteSettings = session?.role === 'admin';

  useEffect(() => {
    if (!canManageStripe) {
      return;
    }

    let isActive = true;

    async function loadStripeSettings() {
      try {
        setIsLoadingStripe(true);
        setStripeError(null);
        const nextSettings = await getStripeSettings();
        if (!isActive) {
          return;
        }

        setStripeSettings(nextSettings);
        setStripeMode(nextSettings.stripe_mode);
        setConnectAccountId(nextSettings.stripe_connect_account_id ?? '');
      } catch (loadError) {
        if (isActive) {
          setStripeError(loadError instanceof Error ? loadError.message : 'Unable to load Stripe setup right now.');
        }
      } finally {
        if (isActive) {
          setIsLoadingStripe(false);
        }
      }
    }

    void loadStripeSettings();

    return () => {
      isActive = false;
    };
  }, [canManageStripe]);

  useEffect(() => {
    if (!canManageSiteSettings) {
      return;
    }

    let isActive = true;

    async function loadSiteSettings() {
      try {
        setIsLoadingSiteSettings(true);
        setSiteSettingsError(null);
        const settings = await getSiteSettings();
        if (!isActive) {
          return;
        }

        setSiteName(settings?.site_name ?? '');
        setNotificationEmail(settings?.contact_email ?? settings?.site_config?.email ?? '');
      } catch (loadError) {
        if (isActive) {
          setSiteSettingsError(loadError instanceof Error ? loadError.message : 'Unable to load site settings right now.');
        }
      } finally {
        if (isActive) {
          setIsLoadingSiteSettings(false);
        }
      }
    }

    void loadSiteSettings();

    return () => {
      isActive = false;
    };
  }, [canManageSiteSettings]);

  async function handlePasswordSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    if (newPassword.length < 8) {
      setError('The new password must be at least 8 characters.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('The new password and confirmation do not match.');
      return;
    }

    setIsSubmitting(true);

    try {
      await changePassword(currentPassword, newPassword);
      setSuccess('Password updated successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update the password right now.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function refreshStripeSettings() {
    if (!canManageStripe) {
      return;
    }

    try {
      setIsLoadingStripe(true);
      const nextSettings = await getStripeSettings();
      setStripeSettings(nextSettings);
      setStripeMode(nextSettings.stripe_mode);
      setConnectAccountId(nextSettings.stripe_connect_account_id ?? '');
    } catch (loadError) {
      setStripeError(loadError instanceof Error ? loadError.message : 'Unable to refresh Stripe setup right now.');
    } finally {
      setIsLoadingStripe(false);
    }
  }

  async function handleStripeSave(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStripeError(null);
    setStripeSuccess(null);

    const normalizedAccountId = connectAccountId.trim();
    if (normalizedAccountId && !/^acct_[A-Za-z0-9]+$/.test(normalizedAccountId)) {
      setStripeError('Stripe account IDs must start with acct_.');
      return;
    }

    try {
      setIsSavingStripe(true);
      const nextSettings = await updateStripeSettings({
        stripe_mode: stripeMode,
        stripe_connect_account_id: normalizedAccountId || null,
      });
      setStripeSettings(nextSettings);
      setStripeMode(nextSettings.stripe_mode);
      setConnectAccountId(nextSettings.stripe_connect_account_id ?? '');
      setStripeSuccess(
        nextSettings.stripe_connect_account_id
          ? 'Stripe Connect account saved for the current payment mode.'
          : 'Stripe Connect account cleared for the current payment mode.',
      );
    } catch (saveError) {
      setStripeError(saveError instanceof Error ? saveError.message : 'Unable to save Stripe setup right now.');
    } finally {
      setIsSavingStripe(false);
    }
  }

  async function handleLaunchOnboarding() {
    if (!session) {
      return;
    }

    setStripeError(null);
    setStripeSuccess(null);

    try {
      setIsLaunchingOnboarding(true);
      const currentUrl = window.location.href;
      const onboarding = await startStripeConnectOnboarding({
        return_url: currentUrl,
        refresh_url: currentUrl,
        email: session.email,
        mode: stripeMode,
      });

      setStripeSuccess(`Opening Stripe onboarding for ${onboarding.account_id}.`);
      window.open(onboarding.onboarding_url, '_blank', 'noopener,noreferrer');
      await refreshStripeSettings();
    } catch (launchError) {
      setStripeError(
        launchError instanceof Error ? launchError.message : 'Unable to start Stripe onboarding right now.',
      );
    } finally {
      setIsLaunchingOnboarding(false);
    }
  }

  async function handleSiteSettingsSave(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSiteSettingsError(null);
    setSiteSettingsSuccess(null);

    if (!siteName.trim()) {
      setSiteSettingsError('Site name is required before saving the notification email.');
      return;
    }

    try {
      setIsSavingSiteSettings(true);
      const settings = await updateSiteSettings({
        site_name: siteName.trim(),
        contact_email: notificationEmail.trim() || null,
      });
      setSiteName(settings?.site_name ?? siteName.trim());
      setNotificationEmail(settings?.contact_email ?? settings?.site_config?.email ?? '');
      setSiteSettingsSuccess(
        (settings?.contact_email ?? settings?.site_config?.email)
          ? 'Global notification email saved.'
          : 'Global notification email cleared.',
      );
    } catch (saveError) {
      setSiteSettingsError(saveError instanceof Error ? saveError.message : 'Unable to save site settings right now.');
    } finally {
      setIsSavingSiteSettings(false);
    }
  }

  const currentModeAccountId =
    stripeMode === 'live'
      ? (stripeSettings?.stripe_connect_account_id_live ?? null)
      : (stripeSettings?.stripe_connect_account_id_test ?? null);

  if (!session) {
    return null;
  }

  return (
    <motion.div
      className="portal-page"
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      <section className="portal-section portal-section--feature">
        <div className="portal-section-heading">
          <div>
            <p className="info-kicker">Account</p>
            <h1>Manage your live Victoria&apos;s Place portal access.</h1>
          </div>
          <span className="portal-role-badge">{getSiteRoleLabel(session.role)}</span>
        </div>

        <div className="portal-account-grid">
          <div className="portal-account-card">
            <p className="info-kicker">Signed in as</p>
            <h2>{session.name}</h2>
            <p>{session.email}</p>
            {session.companyId ? <p>Company ID: {session.companyId}</p> : null}
          </div>

          <div className="portal-account-card">
            <p className="info-kicker">Current role</p>
            <h2>{roleConfig.label}</h2>
            <p>{roleConfig.supportNote}</p>
          </div>
        </div>
      </section>

      <div className="portal-meta-grid">
        <section className="portal-section">
          <p className="info-kicker">Password</p>
          <h2>Update your Bliss site-user password.</h2>
          <p className="portal-feature-copy">
            This form now submits to the live Bliss <code>/app/auth/change-password</code> endpoint for Victoria&apos;s Place.
          </p>

          <form className="portal-form-grid" onSubmit={handlePasswordSubmit}>
            <label className="auth-field">
              <span>Current password</span>
              <input
                className="auth-input"
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                required
              />
            </label>

            <label className="auth-field">
              <span>New password</span>
              <input
                className="auth-input"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                required
              />
            </label>

            <label className="auth-field">
              <span>Confirm new password</span>
              <input
                className="auth-input"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                required
              />
            </label>

            {error ? <div className="auth-error">{error}</div> : null}
            {success ? <div className="auth-success">{success}</div> : null}

            <button type="submit" className="button-primary tap-target portal-form-submit" disabled={isSubmitting}>
              {isSubmitting ? 'Updating password...' : 'Update password'}
            </button>
          </form>
        </section>

        <section className="portal-section">
          <p className="info-kicker">Runtime</p>
          <h2>Current Bliss project context.</h2>
          <div className="portal-runtime-list">
            <div className="portal-runtime-row">
              <span>Project ID</span>
              <strong>{runtime?.projectId ?? 'Unavailable'}</strong>
            </div>
            <div className="portal-runtime-row">
              <span>Role source</span>
              <strong>{runtime?.roleSource ?? 'Unavailable'}</strong>
            </div>
            <div className="portal-runtime-row">
              <span>Default role</span>
              <strong>{runtime ? getSiteRoleLabel(runtime.defaultRole) : 'Unavailable'}</strong>
            </div>
            <div className="portal-runtime-row">
              <span>Allowed roles</span>
              <strong>{runtime ? runtime.allowedRoles.map(getSiteRoleLabel).join(', ') : 'Unavailable'}</strong>
            </div>
          </div>
        </section>
      </div>

      {canManageStripe ? (
        <section className="portal-section">
          <div className="portal-section-heading">
            <div>
              <p className="info-kicker">Stripe Connect</p>
              <h2>Add the connected account that should receive event and donation payments.</h2>
            </div>

            <div className="portal-inline-actions">
              <button
                type="button"
                className="button-secondary tap-target"
                onClick={() => void refreshStripeSettings()}
                disabled={isLoadingStripe || isSavingStripe || isLaunchingOnboarding}
              >
                {isLoadingStripe ? 'Refreshing...' : 'Refresh Stripe setup'}
              </button>
              <button
                type="button"
                className="button-primary tap-target"
                onClick={() => void handleLaunchOnboarding()}
                disabled={isLoadingStripe || isSavingStripe || isLaunchingOnboarding}
              >
                {isLaunchingOnboarding ? 'Opening onboarding...' : 'Start Stripe onboarding'}
              </button>
            </div>
          </div>

          <p className="portal-feature-copy">
            Public donations and event payments both rely on the project&apos;s Stripe Connect account. You can launch
            onboarding here or save an existing Stripe account ID if Victoria&apos;s Place already has one.
          </p>

          {stripeError ? <div className="auth-error">{stripeError}</div> : null}
          {stripeSuccess ? <div className="auth-success">{stripeSuccess}</div> : null}

          <div className="portal-account-grid">
            <div className="portal-account-card">
              <p className="info-kicker">Current payment mode</p>
              <h2>{stripeMode === 'live' ? 'Live mode' : 'Test mode'}</h2>
              <p>
                {currentModeAccountId
                  ? `Connected account ready for ${stripeMode} payments.`
                  : `No Stripe Connect account is configured for ${stripeMode} payments yet.`}
              </p>
            </div>

            <div className="portal-account-card">
              <p className="info-kicker">Stored accounts</p>
              <div className="portal-runtime-list">
                <div className="portal-runtime-row">
                  <span>Test account</span>
                  <strong>{stripeSettings?.stripe_connect_account_id_test ?? 'Not connected'}</strong>
                </div>
                <div className="portal-runtime-row">
                  <span>Live account</span>
                  <strong>{stripeSettings?.stripe_connect_account_id_live ?? 'Not connected'}</strong>
                </div>
              </div>
            </div>
          </div>

          <form className="portal-form-grid portal-form-grid--stripe" onSubmit={handleStripeSave}>
            <label className="auth-field">
              <span>Stripe mode</span>
              <select
                className="auth-input"
                value={stripeMode}
                onChange={(event) => {
                  const nextMode = event.target.value as 'test' | 'live';
                  setStripeMode(nextMode);
                  setConnectAccountId(
                    nextMode === 'live'
                      ? (stripeSettings?.stripe_connect_account_id_live ?? '')
                      : (stripeSettings?.stripe_connect_account_id_test ?? ''),
                  );
                  setStripeError(null);
                  setStripeSuccess(null);
                }}
                disabled={isLoadingStripe || isSavingStripe || isLaunchingOnboarding}
              >
                <option value="test">Test</option>
                <option value="live">Live</option>
              </select>
            </label>

            <label className="auth-field portal-field-span-full">
              <span>Connected account ID</span>
              <input
                className="auth-input"
                type="text"
                placeholder="acct_..."
                value={connectAccountId}
                onChange={(event) => setConnectAccountId(event.target.value)}
                disabled={isLoadingStripe || isSavingStripe || isLaunchingOnboarding}
              />
            </label>

            <button type="submit" className="button-secondary tap-target portal-form-submit" disabled={isLoadingStripe || isSavingStripe || isLaunchingOnboarding}>
              {isSavingStripe ? 'Saving Stripe setup...' : 'Save Stripe settings'}
            </button>
          </form>
        </section>
      ) : null}

      <section className="portal-section">
        <p className="info-kicker">Support</p>
        <h2>Need help with access or portal changes?</h2>
        <p className="portal-feature-copy">
          Contact Victoria&apos;s Place if a role needs to change, if event details look incorrect,
          or if additional site-user tools need to be added for families, volunteers, or vendors.
        </p>
        <div className="portal-inline-actions">
          <Link to="/contact" className="button-primary tap-target">
            Contact the team
          </Link>
          <Link to="/portal/events" className="button-secondary tap-target">
            Return to events
          </Link>
        </div>
      </section>

      {canManageSiteSettings ? (
        <section className="portal-section">
          <div className="portal-section-heading">
            <div>
              <p className="info-kicker">Notifications</p>
              <h2>Set one global email for project-wide alerts.</h2>
            </div>
          </div>

          <p className="portal-feature-copy">
            This email is used as the shared site-owner notification address for donation alerts, contact and facility-request
            submissions, and any other project-level notifications that do not have their own dedicated override.
          </p>

          {siteSettingsError ? <div className="auth-error">{siteSettingsError}</div> : null}
          {siteSettingsSuccess ? <div className="auth-success">{siteSettingsSuccess}</div> : null}

          <form className="portal-form-grid" onSubmit={handleSiteSettingsSave}>
            <label className="auth-field">
              <span>Site name</span>
              <input
                className="auth-input"
                type="text"
                value={siteName}
                onChange={(event) => setSiteName(event.target.value)}
                disabled={isLoadingSiteSettings || isSavingSiteSettings}
                required
              />
            </label>

            <label className="auth-field">
              <span>Global notification email</span>
              <input
                className="auth-input"
                type="email"
                placeholder="siteowner@example.com"
                value={notificationEmail}
                onChange={(event) => setNotificationEmail(event.target.value)}
                disabled={isLoadingSiteSettings || isSavingSiteSettings}
              />
            </label>

            <button type="submit" className="button-secondary tap-target portal-form-submit" disabled={isLoadingSiteSettings || isSavingSiteSettings}>
              {isSavingSiteSettings ? 'Saving notification email...' : 'Save notification email'}
            </button>
          </form>
        </section>
      ) : null}
    </motion.div>
  );
}
